import type { AnyAction, ThunkAction } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getParentPathWithoutFilters } from 'src/services/contentstack/taxonomyNeighborhood/getParentPathWithoutFilters';
import { fetchNeighborhoodMap } from 'src/services/productlistings-api/fetchNeighborhoodMap';
import { fetchTaxonomyPlpContentEntry } from 'src/services/productlistings-api/fetchTaxonomyPlpContentEntry';
import { selectTaxonomyContentEntry } from 'src/utils/selectTaxonomyContentEntry';
import { actions } from '../slice';

interface SyncTaxonomyContentArgs {
  newPath: string;
  previousPath: string;
}

/**
 * Orchestrates the client-side content update for a shop-PLP URL change:
 * 1. Refetch + merge the neighbourhood map only when the neighbourhood root changed.
 * 2. Re-select the matched entry for the new path.
 * 3. Fetch + merge that entry's content if it is not already loaded.
 * All merges are additive, so out-of-order responses are safe.
 */
export const syncTaxonomyContentFromURL = ({
  newPath,
  previousPath,
}: SyncTaxonomyContentArgs): ThunkAction<
  Promise<void>,
  ReduxApplicationState,
  undefined,
  AnyAction
> => {
  return async (dispatch, getState) => {
    const neighborhoodChanged =
      getParentPathWithoutFilters(previousPath) !== getParentPathWithoutFilters(newPath);

    if (neighborhoodChanged) {
      const mapEntries = await fetchNeighborhoodMap(newPath);

      if (mapEntries.length > 0) {
        dispatch(actions.mergeNeighborhoodMap(mapEntries));
      }
    }

    const matched = selectTaxonomyContentEntry(newPath, getState().content.entries ?? []);

    if (!matched || matched.content) {
      return;
    }

    const content = await fetchTaxonomyPlpContentEntry(matched.url);

    if (content) {
      dispatch(actions.mergeEntryContent(content));
    }
  };
};
